/**
 * Local (desktop) embedding for the Embed Captions modal: the parts of the "Local" option
 * that don't belong in the component. The bundled cc-embed tool does the work
 * (src/lib/ccEmbed.js); this module decides what to ask it for.
 *
 * Timing: Closed Caption Creator's event times are relative to the media's first frame.
 * The captions file is encoded at incode 00:00:00:00, as for the Server option, and
 * cc-embed is told the first video frame is 00:00:00:00 too, so captions line up with the
 * picture whatever start timecode the file itself carries.
 */

import encode from "@cclib/functions/encode.js";
import defaults from "@cclib/lib/defaults.js";
import formats from "@cclib/lib/formats.js";
import replaceTrialText from "@cclib/functions/eventGroups/replaceTrialText.js";
import wrapStyledTextWithSpan from "@cclib/functions/quill/wrapStyledTextWithSpan.js";

/* Video codecs (MP4/MOV sample entries) that can carry A/53 SEI. */
const SEI_CODECS = ["avc1", "avc3", "hvc1", "hev1"];
const MOV_FORMATS = ["mp4", "quicktime"];

/** Extensions Local embedding accepts (cc-embed's containers). */
export const LOCAL_EMBED_EXTENSIONS = ".mov,.mp4,.m4v,.mxf,.ts,.m2ts,.mts,.mpg,.mpeg,.m2p";

/**
 * What a probed file allows.
 * @param {object} probe  cc-embed's probe report
 */
export function describeTarget(probe) {
    const format = probe?.container?.format || "";
    const video = probe?.streams?.find((s) => s.kind === "video");
    const captions = probe?.captions || [];
    return {
        format,
        isMov: MOV_FORMATS.includes(format),
        isMxf: format === "mxf",
        canSei: MOV_FORMATS.includes(format) && SEI_CODECS.includes(String(video?.codec || "").toLowerCase()),
        videoCodec: video?.codec || "",
        frameRate: video?.frame_rate || null,
        has608708: captions.some((c) => c.payload !== "op47_sdp"),
        hasOp47: captions.some((c) => c.payload === "op47_sdp"),
    };
}

/** Whether `standard` ("cea608708" or "op47") will replace captions the file already has. */
export function hasExistingCaptions(target, standard) {
    return standard === "op47" ? target.hasOp47 : target.has608708;
}

/**
 * The output path: `cc_<name>.<input extension>` in `folder` (the Server option's
 * download name). Never the input itself.
 */
export function localOutputPath(inputPath, folder, name) {
    const sep = inputPath.includes("\\") ? "\\" : "/";
    const base = inputPath.split(/[\\/]/).pop();
    const dot = base.lastIndexOf(".");
    const ext = dot > 0 ? base.slice(dot) : "";
    const safe = (name || (dot > 0 ? base.slice(0, dot) : base)).replace(/[<>:"/\\|?*\x00-\x1f]/g, "_").trim() || "captions";
    const dir = (folder || inputPath.slice(0, inputPath.length - base.length)).replace(/[\\/]+$/, "");
    let output = `${dir}${sep}cc_${safe}${ext}`;
    if (output.toLowerCase() === inputPath.toLowerCase()) {
        output = `${dir}${sep}cc_${safe}_captioned${ext}`;
    }
    return output;
}

/** The timecode of the first video frame, as cc-embed's --start-tc takes it. */
export function localStartTc(dropFrame) {
    return dropFrame ? "00:00:00;00" : "00:00:00:00";
}

/* Selects `value` for the profile encode option called `name`. */
function setFormatOption(formatOptions, name, value) {
    const option = formatOptions.find((o) => o.name === name);
    if (!option) throw new Error(`EBU STL encode option "${name}" not found`);
    option.selected = value;
}

function hexToBytes(hex) {
    const bytes = new Uint8Array(hex.length >> 1);
    for (let i = 0; i < bytes.length; i++) {
        bytes[i] = parseInt(hex.substr(i * 2, 2), 16);
    }
    return bytes;
}

/**
 * Encodes an event group (already copied, ordered and checked) into the file cc-embed
 * embeds: MCC for 608/708 (CC1 + 708 service 1, as the Server option sends), or EBU STL
 * for OP-47 teletext.
 *
 * The OP-47 STL is a teletext one (Display Standard Code "Level-1 teletext"): every row
 * boxed, as subtitle pages need, and double height when `teletext.doubleHeight` is set
 * (the layout Telestream's inserter uses). The profile's default, "Open subtitling" without
 * double height, put a two-line subtitle on adjacent rows, and Telestream Switch showed
 * only its first line (customer example_03, cc-embed docs/triage.md).
 *
 * @param {{doubleHeight: boolean}} [opts.teletext]  OP-47 only; default double height
 * @returns {Promise<{captionsData: string|Uint8Array, captionsName: string}>}
 */
export async function encodeCaptionsFile(eventGroup, { standard, frameRate, dropFrame, inTrial, window, teletext = { doubleHeight: true } }) {
    let group = inTrial ? replaceTrialText(eventGroup) : eventGroup;
    group.events.forEach((event, index, events) => {
        events[index].text = wrapStyledTextWithSpan(event.text);
    });

    const profileName = standard === "op47" ? "EBU STL" : "MacCaption 608/708";
    const profile = formats.profileMapping[profileName];
    const formatOptions = structuredClone(profile.options.encode);
    if (standard === "op47") {
        setFormatOption(formatOptions, "Display Standard Code", "Level-1 teletext");
        setFormatOption(formatOptions, "Double Height Characters", teletext.doubleHeight ? "yes" : "no");
    }
    const options = new defaults.options({
        profile: profile.name,
        formatOptions,
        frameRate: frameRate,
        dropFrame: dropFrame,
        target_profile: profile.name,
        source_profile: "closedCaptionProject",
        incode: "00:00:00:00",
        window: window,
    });
    const output = await encode(group, options);
    if (standard === "op47") {
        return { captionsData: hexToBytes(output), captionsName: "captions.stl" };
    }
    return { captionsData: output, captionsName: "captions.mcc" };
}
